type Base64URL = string;

type JSONCredentialDescriptor = Omit<PublicKeyCredentialDescriptor, "id"> & {
  id: Base64URL;
};

export type JSONCreationOptions = Omit<
  PublicKeyCredentialCreationOptions,
  "challenge" | "user" | "excludeCredentials"
> & {
  challenge: Base64URL;
  user: Omit<PublicKeyCredentialUserEntity, "id"> & { id: Base64URL };
  excludeCredentials?: JSONCredentialDescriptor[];
};

export type JSONRequestOptions = Omit<
  PublicKeyCredentialRequestOptions,
  "challenge" | "allowCredentials"
> & {
  challenge: Base64URL;
  allowCredentials?: JSONCredentialDescriptor[];
};

export interface JSONPublicKeyCredential {
  id: string;
  rawId: Base64URL;
  type: PublicKeyCredentialType;
  authenticatorAttachment: AuthenticatorAttachment | null;
  clientExtensionResults: AuthenticationExtensionsClientOutputs;
  response: Record<string, unknown>;
}

function decodeBase64URL(value: string): ArrayBuffer {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/") + padding;
  let decoded: string;
  try {
    decoded = window.atob(base64);
  } catch (cause) {
    throw new Error("The server returned invalid passkey data.", { cause });
  }
  const bytes = Uint8Array.from(decoded, (character) =>
    character.charCodeAt(0),
  );
  return bytes.buffer;
}

function encodeBase64URL(value: ArrayBuffer): string {
  const bytes = new Uint8Array(value);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return window
    .btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
}

function credentialDescriptor(
  descriptor: JSONCredentialDescriptor,
): PublicKeyCredentialDescriptor {
  return { ...descriptor, id: decodeBase64URL(descriptor.id) };
}

export function parseCreationOptions(
  options: JSONCreationOptions,
): PublicKeyCredentialCreationOptions {
  return {
    ...options,
    challenge: decodeBase64URL(options.challenge),
    user: { ...options.user, id: decodeBase64URL(options.user.id) },
    excludeCredentials: options.excludeCredentials?.map(credentialDescriptor),
  };
}

export function parseRequestOptions(
  options: JSONRequestOptions,
): PublicKeyCredentialRequestOptions {
  return {
    ...options,
    challenge: decodeBase64URL(options.challenge),
    allowCredentials: options.allowCredentials?.map(credentialDescriptor),
  };
}

function commonCredential(
  credential: PublicKeyCredential,
): Omit<JSONPublicKeyCredential, "response"> {
  return {
    id: credential.id,
    rawId: encodeBase64URL(credential.rawId),
    type: credential.type as PublicKeyCredentialType,
    authenticatorAttachment:
      credential.authenticatorAttachment as AuthenticatorAttachment | null,
    clientExtensionResults: credential.getClientExtensionResults(),
  };
}

export function serializeRegistrationCredential(
  credential: PublicKeyCredential,
): JSONPublicKeyCredential {
  const response = credential.response;
  if (!(response instanceof AuthenticatorAttestationResponse)) {
    throw new Error(
      "The authenticator returned an invalid registration response.",
    );
  }
  return {
    ...commonCredential(credential),
    response: {
      attestationObject: encodeBase64URL(response.attestationObject),
      clientDataJSON: encodeBase64URL(response.clientDataJSON),
      transports:
        typeof response.getTransports === "function"
          ? response.getTransports()
          : undefined,
    },
  };
}

export function serializeAuthenticationCredential(
  credential: PublicKeyCredential,
): JSONPublicKeyCredential {
  const response = credential.response;
  if (!(response instanceof AuthenticatorAssertionResponse)) {
    throw new Error("The authenticator returned an invalid sign-in response.");
  }
  return {
    ...commonCredential(credential),
    response: {
      authenticatorData: encodeBase64URL(response.authenticatorData),
      clientDataJSON: encodeBase64URL(response.clientDataJSON),
      signature: encodeBase64URL(response.signature),
      userHandle: response.userHandle
        ? encodeBase64URL(response.userHandle)
        : null,
    },
  };
}

function requireCredential(
  credential: Credential | null,
  action: "create" | "get",
): PublicKeyCredential {
  if (!(credential instanceof PublicKeyCredential)) {
    throw new Error(
      action === "create"
        ? "No passkey was created."
        : "No passkey was selected.",
    );
  }
  return credential;
}

export async function createPasskey(
  options: JSONCreationOptions,
): Promise<JSONPublicKeyCredential> {
  if (!window.PublicKeyCredential || !navigator.credentials) {
    throw new Error("This browser does not support passkeys.");
  }
  const credential = requireCredential(
    await navigator.credentials.create({
      publicKey: parseCreationOptions(options),
    }),
    "create",
  );
  return serializeRegistrationCredential(credential);
}

export async function getPasskey(
  options: JSONRequestOptions,
): Promise<JSONPublicKeyCredential> {
  if (!window.PublicKeyCredential || !navigator.credentials) {
    throw new Error("This browser does not support passkeys.");
  }
  const credential = requireCredential(
    await navigator.credentials.get({
      publicKey: parseRequestOptions(options),
    }),
    "get",
  );
  return serializeAuthenticationCredential(credential);
}

export function passkeyErrorMessage(cause: unknown): string {
  if (cause instanceof DOMException && cause.name === "NotAllowedError") {
    return "Passkey verification was cancelled or timed out. Please try again.";
  }
  if (cause instanceof Error && cause.message) return cause.message;
  return "Passkey verification failed. Please try again.";
}
